import { Component, Input, OnInit } from '@angular/core';
import IPriorityViewPublicApi = app.interfaces.IPriorityViewPublicApi;
import { PageOptimizationService } from '../../../../../services/page-optimization.service';
import { PageOptimizationAudit, PageOptimizationFeedbackSources, PageOptimizationTaskFeedback } from '../../../../../shared/models/page-optimizations/page-optimizations';
import { finalize } from 'rxjs';
import TaskTypes = Boo.Objects.Enums.TaskTypeEnum;

@Component({
  selector: 'app-components-page-optimization-feedback',
  templateUrl: './page-optimization-feedback.component.html',
  styleUrls: ['./page-optimization-feedback.component.scss']
})
export class PageOptimizationFeedbackComponent implements IActivatable, OnInit {
  @Input() taskFeedbackId: number;

  totalAdditions = 0;
  totalRemovals = 0;
  loading = true;
  taskTypeName = '';
  feedbackSource = '';
  taskFeedback: PageOptimizationTaskFeedback;

  private diffCounts = new Map<string, { additions: number; removals: number }>();

  private readonly taskTypeNames: Partial<Record<TaskTypes, string>> = {
    [TaskTypes.PageCreationCreateContent]: 'Page Creation - Create Content',
    [TaskTypes.PageCreationRewriteContent]: 'Page Creation - Rewrite Content',
    [TaskTypes.PageOptimizationCreateContent]: 'Page Optimization - Create Content',
    [TaskTypes.PageOptimizationRewriteContent]: 'Page Optimization - Rewrite Content',
  };

  private readonly feedbackSources: Partial<Record<PageOptimizationFeedbackSources, string>> = {
    [PageOptimizationFeedbackSources.Customer]: 'Customer',
    [PageOptimizationFeedbackSources.Reviewer]: 'Reviewer'
  };

  private priorityViewApi: IPriorityViewPublicApi;

  constructor(private pageOptimizationService: PageOptimizationService) { }

  ngOnInit(): void {
    this.pageOptimizationService.getTaskFeedback(this.taskFeedbackId)
      .pipe(finalize(() => this.loading = false))
      .subscribe({
        next: (taskFeedback: PageOptimizationTaskFeedback) => {
          this.taskTypeName = this.taskTypeNames[taskFeedback.ContentTaskType] ?? 'Page Optimization';
          this.feedbackSource = this.feedbackSources[taskFeedback.FeedbackSource] ?? '';
          this.taskFeedback = taskFeedback;
        },
        error: (err) => toastr.error('Failed to load feedback:', err)
      });
  }

  canActivate(user: Boo.Objects.User, partner: Boo.Objects.Partner, params: any): boolean {
    this.priorityViewApi = params.priorityViewApi;
    return true;
  }

  generateAuditSummary(audit: PageOptimizationAudit): string {
    if (!audit) {
      return '';
    }
    return [
      `Headings Structure: ${this.passOrFail(audit.headingsStructure)}`,
      `Meta Robots: ${this.passOrFail(audit.metaRobots)}`,
      `Conversion Language in Place: ${this.passOrFail(audit.conversionLanguageInPlace)}`,
      `Duplicate Content (Canonical Tag): ${this.passOrFail(audit.duplicateContent)}`,
      `Robots.txt File: ${this.passOrFail(audit.robotsTxtFile)}`,
      `Redirects: ${this.passOrFail(audit.redirects)}`,
      `Image Optimization: ${this.passOrFail(audit.imageOptimization)}`,
      `URL Structure: ${this.passOrFail(audit.urlStructure)}`
    ].join('<br>');
  }

  formatKeywords(keywords: string[]): string {
    if (!keywords?.length) {
      return '';
    }
    return [...keywords].sort((a, b) => a.localeCompare(b)).join(', ');
  }

  close() {
    this.priorityViewApi.resolve();
  }

  onDiffComputed(heading: string, event: { additions: number; removals: number }): void {
    this.diffCounts.set(heading, event);
    this.totalAdditions = 0;
    this.totalRemovals = 0;
    this.diffCounts.forEach(counts => {
      this.totalAdditions += counts.additions;
      this.totalRemovals += counts.removals;
    });
  }

  private passOrFail(value: boolean): string {
    return value ? 'Pass' : 'Fail';
  }
}
